interface PerformanceMetric {
  id: string;
  timestamp: number;
  type: 'navigation' | 'api' | 'render' | 'memory';
  value: number;
  context: any;
}

interface SystemHealth {
  score: number; // 0-100
  trends: {
    navigation: 'improving' | 'stable' | 'degrading';
    memory: 'improving' | 'stable' | 'degrading';
    api: 'improving' | 'stable' | 'degrading';
  };
  predictions: {
    nextIssue: number; // minutes until predicted issue
    confidence: number; // 0-1
    type: string;
  };
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];
  private maxMetrics = 1000;

  constructor() {
    this.initializeObservers();
    this.startMemoryMonitoring();
  }

  private initializeObservers(): void {
    // Navigation timing
    if ('PerformanceObserver' in window) {
      const navObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach(entry => {
          if (entry.entryType === 'navigation') {
            this.recordMetric('navigation', entry.duration, {
              type: 'page_load',
              url: window.location.pathname
            });
          }
        });
      });
      navObserver.observe({ entryTypes: ['navigation'] });
      this.observers.push(navObserver);

      // Resource timing
      const resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach(entry => {
          if (entry.duration > 1000) { // Slow resources
            this.recordMetric('api', entry.duration, {
              type: 'slow_resource',
              name: entry.name
            });
          }
        });
      });
      resourceObserver.observe({ entryTypes: ['resource'] });
      this.observers.push(resourceObserver);
    }
  }

  private startMemoryMonitoring(): void {
    setInterval(() => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        this.recordMetric('memory', memory.usedJSHeapSize / 1024 / 1024, {
          total: memory.totalJSHeapSize / 1024 / 1024,
          limit: memory.jsHeapSizeLimit / 1024 / 1024
        });
      }
    }, 30000); // Every 30 seconds
  }

  recordMetric(type: PerformanceMetric['type'], value: number, context: any = {}): void {
    const metric: PerformanceMetric = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      type,
      value,
      context
    };

    this.metrics.push(metric);
    this.maintainMetricsSize();
    this.analyzeForAnomalies(metric);
  }

  getSystemHealth(): SystemHealth {
    const now = Date.now();
    const recentMetrics = this.metrics.filter(m => now - m.timestamp < 300000); // Last 5 minutes

    return {
      score: this.calculateHealthScore(recentMetrics),
      trends: this.analyzeTrends(recentMetrics),
      predictions: this.predictNextIssue(recentMetrics)
    };
  }

  getMetrics(type?: PerformanceMetric['type'], limit = 100): PerformanceMetric[] {
    let filtered = type ? this.metrics.filter(m => m.type === type) : this.metrics;
    return filtered.slice(-limit);
  }

  private calculateHealthScore(metrics: PerformanceMetric[]): number {
    if (metrics.length === 0) return 100;

    let score = 100;
    
    // Navigation performance (weight: 40%)
    const navMetrics = metrics.filter(m => m.type === 'navigation');
    const avgNavTime = navMetrics.reduce((sum, m) => sum + m.value, 0) / navMetrics.length || 0;
    if (avgNavTime > 3000) score -= 20;
    else if (avgNavTime > 1000) score -= 10;

    // Memory usage (weight: 30%) - OPTIMIZED: Increased thresholds
    const memMetrics = metrics.filter(m => m.type === 'memory');
    const latestMem = memMetrics[memMetrics.length - 1];
    if (latestMem && latestMem.value > 200) score -= 15; // Changed from 100 to 200
    else if (latestMem && latestMem.value > 150) score -= 8; // Changed from 50 to 150

    // API performance (weight: 30%)
    const apiMetrics = metrics.filter(m => m.type === 'api');
    const slowApis = apiMetrics.filter(m => m.value > 2000).length;
    if (slowApis > 3) score -= 15;
    else if (slowApis > 1) score -= 8;

    return Math.max(0, Math.min(100, score));
  }

  private analyzeTrends(metrics: PerformanceMetric[]): SystemHealth['trends'] {
    const getTrend = (type: PerformanceMetric['type']) => {
      const typeMetrics = metrics.filter(m => m.type === type).slice(-10);
      if (typeMetrics.length < 3) return 'stable';

      const recent = typeMetrics.slice(-3).reduce((sum, m) => sum + m.value, 0) / 3;
      const older = typeMetrics.slice(0, 3).reduce((sum, m) => sum + m.value, 0) / 3;

      if (recent < older * 0.9) return 'improving';
      if (recent > older * 1.1) return 'degrading';
      return 'stable';
    };

    return {
      navigation: getTrend('navigation'),
      memory: getTrend('memory'),
      api: getTrend('api')
    };
  }

  private predictNextIssue(metrics: PerformanceMetric[]): SystemHealth['predictions'] {
    // OPTIMIZED: Use longer time window and higher thresholds
    const memoryTrend = metrics.filter(m => m.type === 'memory').slice(-10); // Changed from -5 to -10
    
    if (memoryTrend.length >= 5) { // Changed from 3 to 5
      const growthRate = this.calculateGrowthRate(memoryTrend);
      const currentMemory = memoryTrend[memoryTrend.length - 1]?.value || 0;
      
      // OPTIMIZED: Increased threshold from 80 to 200 MB and confidence from 0.7 to 0.85
      if (growthRate > 0 && currentMemory > 200) {
        const minutesToLimit = (250 - currentMemory) / (growthRate * 60); // Changed limit from 100 to 250
        return {
          nextIssue: Math.max(1, minutesToLimit),
          confidence: 0.85, // Changed from 0.7 to 0.85
          type: 'memory_exhaustion'
        };
      }
    }

    return {
      nextIssue: -1,
      confidence: 0,
      type: 'none'
    };
  }

  private calculateGrowthRate(metrics: PerformanceMetric[]): number {
    if (metrics.length < 2) return 0;
    
    const timeSpan = metrics[metrics.length - 1].timestamp - metrics[0].timestamp;
    const valueChange = metrics[metrics.length - 1].value - metrics[0].value;
    
    return valueChange / (timeSpan / 1000); // per second
  }

  private analyzeForAnomalies(metric: PerformanceMetric): void {
    const recentSimilar = this.metrics
      .filter(m => m.type === metric.type)
      .slice(-10);

    if (recentSimilar.length < 3) return;

    const avg = recentSimilar.reduce((sum, m) => sum + m.value, 0) / recentSimilar.length;
    const threshold = Math.max(avg * 2, 5000); // Min 5 seconds threshold

    if (metric.value > threshold) {
      // Only log in development
      if (import.meta.env.DEV) {
        console.warn(`Performance anomaly detected: ${metric.type} = ${metric.value}ms (avg: ${avg}ms)`);
      }
      
      // Trigger alert
      this.triggerPerformanceAlert(metric, avg);
    }
  }

  private triggerPerformanceAlert(metric: PerformanceMetric, baseline: number): void {
    // OPTIMIZED: Only trigger alerts for high severity issues
    const severity = metric.value > baseline * 3 ? 'high' : 'medium';
    
    // Only dispatch event for high severity
    if (severity === 'high') {
      const alertEvent = new CustomEvent('performance-alert', {
        detail: {
          metric,
          baseline,
          severity
        }
      });
      window.dispatchEvent(alertEvent);
    }
  }

  private maintainMetricsSize(): void {
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }
  }

  cleanup(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.metrics = [];
  }
}

export const performanceMonitor = new PerformanceMonitor();
export type { PerformanceMetric, SystemHealth };
